import React, { useRef, useEffect, useCallback } from "react";
import { useTranslation } from "../../hooks/useTranslation";
import type { CanvasState, Point, CanvasImage, TextBlock } from "./types";

const STORY_ASPECT = 9 / 16;
const CANVAS_WIDTH = 375;
const CANVAS_HEIGHT = CANVAS_WIDTH / STORY_ASPECT;
/** Extra padding (screen px) for touch hit area - makes text blocks easier to grab on mobile */
const TOUCH_HIT_PADDING = 24;
/** Width of left/right edge strip (screen px) for text width resize on touch */
const TEXT_EDGE_HIT = 36;

type Interaction = "none" | "image-drag" | "image-pinch" | "text-drag" | "text-resize";

function getCanvasPoint(
  clientX: number,
  clientY: number,
  rect: DOMRect
): Point {
  const scaleX = CANVAS_WIDTH / rect.width;
  const scaleY = CANVAS_HEIGHT / rect.height;
  return {
    x: (clientX - rect.left) * scaleX,
    y: (clientY - rect.top) * scaleY,
  };
}

function getTouchDistance(t1: React.Touch, t2: React.Touch): number {
  return Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
}

function hitTestImage(
  img: CanvasImage,
  clientX: number,
  clientY: number,
  rect: DOMRect,
  scale: number
): boolean {
  const left = rect.left + (img.x - img.width / 2) * scale;
  const right = rect.left + (img.x + img.width / 2) * scale;
  const top = rect.top + (img.y - img.height / 2) * scale;
  const bottom = rect.top + (img.y + img.height / 2) * scale;
  return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
}

/** Returns "left" | "right" if (clientX, clientY) is in the left/right resize edge of the text block, else null. */
function hitTestTextResizeEdge(
  t: TextBlock,
  clientX: number,
  clientY: number,
  rect: DOMRect,
  scaleS: number
): "left" | "right" | null {
  const tw = (t.width ?? 260) * scaleS;
  const th = (t.height ?? 56) * scaleS;
  const tx = rect.left + t.x * scaleS;
  const ty = rect.top + t.y * scaleS;
  if (clientY < ty - th / 2 - TOUCH_HIT_PADDING || clientY > ty + th / 2 + TOUCH_HIT_PADDING) return null;
  const left = tx - tw / 2;
  const right = tx + tw / 2;
  if (clientX >= left && clientX <= left + TEXT_EDGE_HIT) return "left";
  if (clientX >= right - TEXT_EDGE_HIT && clientX <= right) return "right";
  return null;
}

interface StoryCanvasProps {
  state: CanvasState;
  dimensions: { width: number; height: number };
  onCanvasClick: (p: Point) => void;
  onSelectElement: (id: string | null, type: "text" | "image" | null) => void;
  onStartImageDrag: (img: CanvasImage, canvasX: number, canvasY: number) => void;
  onMoveImageDrag: (canvasX: number, canvasY: number) => void;
  onEndImageDrag: () => void;
  onStartPinch: (img: CanvasImage, distance: number) => void;
  onMovePinch: (distance: number) => void;
  onEndPinch: () => void;
  onStartTextDrag: (id: string, canvasX: number, canvasY: number) => void;
  onMoveTextDrag: (canvasX: number, canvasY: number) => void;
  onEndTextDrag: () => void;
  onStartTextResize: (id: string, edge: "left" | "right", startWidth: number, startX: number) => void;
  onMoveTextResize: (canvasX: number) => void;
  onEndTextResize: () => void;
  onUpdateTextBlock: (id: string, updates: Partial<TextBlock>) => void;
  onDeleteTextBlock: (id: string) => void;
  onDeleteImage: (id: string) => void;
}

const StoryCanvas: React.FC<StoryCanvasProps> = ({
  state,
  dimensions,
  onCanvasClick,
  onSelectElement,
  onStartImageDrag,
  onMoveImageDrag,
  onEndImageDrag,
  onStartPinch,
  onMovePinch,
  onEndPinch,
  onStartTextDrag,
  onMoveTextDrag,
  onEndTextDrag,
  onStartTextResize,
  onMoveTextResize,
  onEndTextResize,
  onUpdateTextBlock,
  onDeleteTextBlock,
  onDeleteImage,
}) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const interactionRef = useRef<Interaction>("none");
  const lastPinchDistanceRef = useRef(0);
  const lastTouchesRef = useRef<{ 0: React.Touch; 1: React.Touch } | null>(null);
  const textareaRefs = useRef<Map<string, HTMLTextAreaElement>>(new Map());
  const lastTextBlocksCountRef = useRef(0);
  const newlyAddedTextIdsRef = useRef<Set<string>>(new Set());

  const scale = dimensions.width / CANVAS_WIDTH;

  // Track newly added text blocks for animation
  useEffect(() => {
    const currentCount = state.textBlocks.length;
    const wasNewTextAdded = currentCount > lastTextBlocksCountRef.current;
    lastTextBlocksCountRef.current = currentCount;

    if (wasNewTextAdded && state.selectedElementType === "text" && state.selectedElementId) {
      // Mark this text block as newly added
      const textId = state.selectedElementId;
      newlyAddedTextIdsRef.current.add(textId);
      
      // Remove from newly added set after animation completes
      setTimeout(() => {
        newlyAddedTextIdsRef.current.delete(textId);
      }, 300);

      const selectedText = state.textBlocks.find((tb) => tb.id === state.selectedElementId);
      if (selectedText) {
        const textarea = textareaRefs.current.get(selectedText.id);
        if (textarea) {
          // Use setTimeout to ensure DOM is ready
          setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(0, textarea.value.length);
          }, 50);
        }
      }
    }
  }, [state.textBlocks.length, state.selectedElementId, state.selectedElementType]);

  const getRect = useCallback(() => contentRef.current?.getBoundingClientRect() ?? new DOMRect(), []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      e.preventDefault();
      const rect = getRect();
      const pt = getCanvasPoint(e.clientX, e.clientY, rect);
      if (e.button !== 0 && e.buttons !== 1) return;

      const scaleS = rect.width / CANVAS_WIDTH;
      const imgReversed = [...state.images].reverse();
      for (const img of imgReversed) {
        if (hitTestImage(img, e.clientX, e.clientY, rect, scaleS)) {
          onSelectElement(img.id, "image");
          interactionRef.current = "image-drag";
          onStartImageDrag(img, pt.x, pt.y);
          return;
        }
      }

      for (let i = state.textBlocks.length - 1; i >= 0; i--) {
        const t = state.textBlocks[i];
        const tw = (t.width ?? 260) * scaleS;
        const th = (t.height ?? 56) * scaleS;
        const tx = rect.left + t.x * scaleS;
        const ty = rect.top + t.y * scaleS;
        if (e.clientX >= tx - tw / 2 && e.clientX <= tx + tw / 2 && e.clientY >= ty - th / 2 && e.clientY <= ty + th / 2) {
          onSelectElement(t.id, "text");
          const edge = hitTestTextResizeEdge(t, e.clientX, e.clientY, rect, scaleS);
          if (edge) {
            interactionRef.current = "text-resize";
            onStartTextResize(t.id, edge, tw / scaleS, pt.x);
          } else {
            interactionRef.current = "text-drag";
            onStartTextDrag(t.id, pt.x, pt.y);
          }
          return;
        }
      }

      onSelectElement(null, null);
      onCanvasClick(pt);
    },
    [state.images, state.textBlocks, getRect, onCanvasClick, onSelectElement, onStartImageDrag, onStartTextDrag, onStartTextResize]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      e.preventDefault();
      const rect = getRect();
      const pt = getCanvasPoint(e.clientX, e.clientY, rect);
      const inter = interactionRef.current;
      if (inter === "image-drag") onMoveImageDrag(pt.x, pt.y);
      else if (inter === "text-drag") onMoveTextDrag(pt.x, pt.y);
      else if (inter === "text-resize") onMoveTextResize(pt.x);
    },
    [getRect, onMoveImageDrag, onMoveTextDrag, onMoveTextResize]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      e.preventDefault();
      const inter = interactionRef.current;
      interactionRef.current = "none";
      if (inter === "image-drag") onEndImageDrag();
      if (inter === "text-drag") onEndTextDrag();
      if (inter === "text-resize") onEndTextResize();
    },
    [onEndImageDrag, onEndTextDrag, onEndTextResize]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const rect = getRect();
      if (e.touches.length === 2) {
        e.preventDefault();
        const dist = getTouchDistance(e.touches[0], e.touches[1]);
        const cx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const cy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        lastPinchDistanceRef.current = dist;
        lastTouchesRef.current = { 0: e.touches[0], 1: e.touches[1] };
        const scaleS = rect.width / CANVAS_WIDTH;
        const imgReversed = [...state.images].reverse();
        for (const img of imgReversed) {
          if (hitTestImage(img, cx, cy, rect, scaleS)) {
            onSelectElement(img.id, "image");
            interactionRef.current = "image-pinch";
            onStartPinch(img, dist);
            break;
          }
        }
        return;
      }
      if (e.touches.length === 1) {
        e.preventDefault();
        const touch = e.touches[0];
        const pt = getCanvasPoint(touch.clientX, touch.clientY, rect);
        const scaleS = rect.width / CANVAS_WIDTH;
        const imgReversed = [...state.images].reverse();
        for (const img of imgReversed) {
          if (hitTestImage(img, touch.clientX, touch.clientY, rect, scaleS)) {
            onSelectElement(img.id, "image");
            interactionRef.current = "image-drag";
            onStartImageDrag(img, pt.x, pt.y);
            return;
          }
        }
        for (let i = state.textBlocks.length - 1; i >= 0; i--) {
          const t = state.textBlocks[i];
          const tw = (t.width ?? 260) * scaleS;
          const th = (t.height ?? 56) * scaleS;
          const tx = rect.left + t.x * scaleS;
          const ty = rect.top + t.y * scaleS;
          const pad = TOUCH_HIT_PADDING;
          if (touch.clientX >= tx - tw / 2 - pad && touch.clientX <= tx + tw / 2 + pad && touch.clientY >= ty - th / 2 - pad && touch.clientY <= ty + th / 2 + pad) {
            onSelectElement(t.id, "text");
            const edge = hitTestTextResizeEdge(t, touch.clientX, touch.clientY, rect, scaleS);
            if (edge) {
              interactionRef.current = "text-resize";
              onStartTextResize(t.id, edge, tw / scaleS, pt.x);
            } else {
              interactionRef.current = "text-drag";
              onStartTextDrag(t.id, pt.x, pt.y);
            }
            return;
          }
        }
        onSelectElement(null, null);
        onCanvasClick(pt);
      }
    },
    [state.images, state.textBlocks, getRect, onCanvasClick, onSelectElement, onStartImageDrag, onStartPinch, onStartTextDrag, onStartTextResize]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const rect = getRect();
      if (e.touches.length === 2 && interactionRef.current === "image-pinch") {
        e.preventDefault();
        const dist = getTouchDistance(e.touches[0], e.touches[1]);
        onMovePinch(dist);
        lastPinchDistanceRef.current = dist;
        return;
      }
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const pt = getCanvasPoint(touch.clientX, touch.clientY, rect);
        if (interactionRef.current === "image-drag") {
          e.preventDefault();
          onMoveImageDrag(pt.x, pt.y);
          return;
        }
        if (interactionRef.current === "text-drag") {
          e.preventDefault();
          onMoveTextDrag(pt.x, pt.y);
          return;
        }
        if (interactionRef.current === "text-resize") {
          e.preventDefault();
          onMoveTextResize(pt.x);
          return;
        }
      }
    },
    [getRect, onMoveImageDrag, onMovePinch, onMoveTextDrag, onMoveTextResize]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 0) {
        const inter = interactionRef.current;
        interactionRef.current = "none";
        if (inter === "image-drag") onEndImageDrag();
        if (inter === "image-pinch") onEndPinch();
        if (inter === "text-drag") onEndTextDrag();
        if (inter === "text-resize") onEndTextResize();
      }
      if (e.touches.length === 1 && interactionRef.current === "image-pinch") {
        interactionRef.current = "none";
        onEndPinch();
      }
    },
    [onEndImageDrag, onEndPinch, onEndTextDrag, onEndTextResize]
  );

  const handleTouchCancel = useCallback(
    () => {
      const inter = interactionRef.current;
      interactionRef.current = "none";
      if (inter === "image-drag") onEndImageDrag();
      if (inter === "image-pinch") onEndPinch();
      if (inter === "text-drag") onEndTextDrag();
      if (inter === "text-resize") onEndTextResize();
    },
    [onEndImageDrag, onEndPinch, onEndTextDrag, onEndTextResize]
  );

  const measureAndUpdateTextSize = useCallback(
    (id: string, el: HTMLTextAreaElement | null) => {
      if (!el) return;
      const measuredW = el.scrollWidth / scale;
      const measuredH = el.scrollHeight / scale;
      const tb = state.textBlocks.find((b) => b.id === id);
      const currentW = tb?.width ?? 260;
      const currentH = tb?.height ?? 56;
      const w = Math.max(80, Math.ceil(Math.max(currentW, measuredW)));
      const h = Math.max(24, Math.ceil(Math.max(currentH, measuredH)));
      onUpdateTextBlock(id, { width: w, height: h });
    },
    [scale, state.textBlocks, onUpdateTextBlock]
  );

  return (
    <div
      className="relative w-full h-full overflow-hidden flex items-center justify-center bg-black"
      style={{
        width: "100%",
        height: "100%",
        touchAction: "none",
      }}
    >
      <div
        ref={contentRef}
        className="relative"
        style={{
          width: CANVAS_WIDTH * scale,
          height: CANVAS_HEIGHT * scale,
          backgroundColor: state.backgroundColor,
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
      >
        {state.images.map((img) => (
          <div
            key={img.id}
            className="absolute select-none"
            style={{
              left: (img.x - img.width / 2) * scale,
              top: (img.y - img.height / 2) * scale,
              width: img.width * scale,
              height: img.height * scale,
              transform: `scale(${img.scaleX}, ${img.scaleY}) rotate(${img.rotation}deg)`,
              pointerEvents: "none",
            }}
          >
            <img
              src={img.src}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover pointer-events-none"
              draggable={false}
            />
          </div>
        ))}

        {state.textBlocks.map((tb) => {
          const isNewlyAdded = newlyAddedTextIdsRef.current.has(tb.id);
          const isDragging = interactionRef.current === "text-drag" && tb.isSelected;
          const isResizing = interactionRef.current === "text-resize" && tb.isSelected;
          const tw = tb.width ?? 260;
          const th = tb.height ?? 56;
          return (
            <div
              key={tb.id}
              className="absolute"
              style={{
                left: (tb.x - tw / 2) * scale,
                top: (tb.y - th / 2) * scale,
                width: tw * scale,
                minHeight: th * scale,
                fontSize: tb.fontSize * scale,
                color: tb.color,
                fontFamily: tb.fontFamily,
                transform: `rotate(${tb.rotation}deg)`,
                direction: "rtl",
                textAlign: tb.textAlign ?? "center",
                border: tb.isSelected ? "2px dashed rgba(255,255,255,0.8)" : "none",
                padding: 4,
                cursor: tb.isSelected ? "text" : "move",
                transition: isDragging || isResizing
                  ? "none"
                  : "left 0.15s ease-out, top 0.15s ease-out, width 0.15s ease-out, transform 0.15s ease-out",
                animation: isNewlyAdded ? "fadeInScale 0.3s ease-out forwards" : "none",
              }}
            onPointerDown={(e) => {
              if (tb.isSelected && (e.target as HTMLElement).tagName === "TEXTAREA") {
                e.stopPropagation();
                return;
              }
            }}
          >
            {tb.isSelected && (
              <>
                <div
                  className="absolute top-1/2 -translate-y-1/2 -left-1 left-0 w-8 z-10 touch-none flex items-center justify-center"
                  style={{ cursor: "ew-resize" }}
                  aria-hidden
                >
                  <span className="w-1 h-8 rounded-full bg-white/70 pointer-events-none" />
                </div>
                <div
                  className="absolute top-1/2 -translate-y-1/2 -right-1 right-0 w-8 z-10 touch-none flex items-center justify-center"
                  style={{ cursor: "ew-resize" }}
                  aria-hidden
                >
                  <span className="w-1 h-8 rounded-full bg-white/70 pointer-events-none" />
                </div>
              </>
            )}
            {tb.isSelected ? (
              <textarea
                ref={(el) => {
                  if (el) {
                    textareaRefs.current.set(tb.id, el);
                  } else {
                    textareaRefs.current.delete(tb.id);
                  }
                }}
                value={tb.text}
                onChange={(e) => {
                  e.stopPropagation();
                  onUpdateTextBlock(tb.id, { text: e.target.value });
                  requestAnimationFrame(() => {
                    measureAndUpdateTextSize(tb.id, textareaRefs.current.get(tb.id) ?? null);
                  });
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  interactionRef.current = "none";
                }}
                onPointerMove={(e) => e.stopPropagation()}
                onFocus={(e) => {
                  e.stopPropagation();
                  onSelectElement(tb.id, "text");
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                className="w-full min-h-[1.2em] bg-transparent resize-none outline-none text-center pointer-events-auto overflow-hidden"
                style={{ 
                  color: tb.color, 
                  fontFamily: tb.fontFamily, 
                  fontSize: "1em", 
                  direction: "rtl",
                  textAlign: tb.textAlign ?? "center",
                  cursor: "text",
                  minHeight: "1.2em",
                }}
                rows={Math.max(1, (tb.text || "").split("\n").length)}
                placeholder={t("story.textPlaceholder")}
                autoFocus={false}
              />
            ) : (
              <span 
                className="select-none block w-full overflow-hidden"
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: tb.color }}
              >
                {tb.text || "‌"}
              </span>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default StoryCanvas;
